import { OmniCardChrome, OmniCardExpandButton } from "@/components/artifacts/OmniCard.tsx";
import { InlineMarkdown } from "@/components/markdown/InlineMarkdown.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { SmartTextarea } from "@/components/messages/Omnibox/SmartTextarea.tsx";
import { OmniDismissButton } from "@/components/artifacts/OmniDismissButton.tsx";
import { useCanvasFollowsCarousel } from "@/components/artifacts/useCanvasFollowsCarousel.ts";
import type { AskUserFormField, UserResponseArtifact } from "@/types/artifacts.ts";
import { decodeMultiSelectValue, encodeMultiSelectValue } from "@/types/artifacts.ts";
import { cn } from "@/lib/utils.ts";
import { FileAttachment } from "@/components/messages/Omnibox/FileAttachment.tsx";
import { ARIA_LABELS, UI_CONSTANTS } from "@/components/messages/Omnibox/constants.ts";
import { useFileAttachment } from "@/components/messages/Omnibox/hooks.ts";
import type { AttachedFile } from "@/components/messages/Omnibox/types.ts";
import { ChevronLeft, ChevronRight, Paperclip, Plus } from "lucide-react";

/** Stand-in setter for a card that takes no files; never called. */
const ignoreFiles = () => {};
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FORM_ARTIFACT_TITLE,
  FormArtifactLightbox,
  useOpenFormArtifact,
} from "./formArtifactSurface.tsx";

/**
 * Omnibox-overlay card for a `userResponse` form artifact, rendered as a stepped
 * wizard — one question per step. Distinct from {@link ArtifactBiscuit} (which
 * renders read-only heavy artifacts with declarative actions); a form is an
 * input, so this owns its own field index and answers, staging them up to the
 * review queue as a single `form` decision committed in the same one-run batch.
 *
 * Navigation is one ←/→ flow across the whole queue: → moves to the next field,
 * or past the last field hands off to the next artifact (or commits on the last
 * card) via `onAdvance`; ← moves to the previous field, or past the first field
 * hands off to the previous artifact via `onPrevArtifact`.
 *
 * Stepping is the fast path, not the only one: the card also offers an expand
 * control that opens the whole form — every question at once — on the canvas,
 * since a wizard can otherwise only ever show one of them (BIG-7271). Answering there submits on the spot rather than staging, and the
 * form then leaves the live set on its own.
 */
export const OmniArtifactFormCard = ({
  artifact,
  threadId,
  stagedValues,
  isSubmitting,
  pagination,
  decidedCount,
  onStageValues,
  onPrevArtifact,
  onAdvance,
  onDismiss,
  onOpenedOnCanvas,
  attachedFiles,
  onFilesChange,
}: {
  artifact: UserResponseArtifact;
  /** The thread the form's answers POST to, for the expanded surfaces. */
  threadId: number;
  /** Answers restored from the staged decision (so navigating away preserves). */
  stagedValues?: Record<string, string>;
  isSubmitting: boolean;
  /**
   * Outer artifact-carousel position, present only in a multi-artifact queue.
   * Drives the "N of M" counter and whether the final step shows a "Done"
   * commit button vs. a next-artifact chevron.
   */
  pagination?: { index: number; total: number };
  /** Decisions staged so far across the queue — for the "· K decided" hint. */
  decidedCount?: number;
  /** Live-stage the current answers as the user edits (mirrors the note input). */
  onStageValues: (values: Record<string, string>) => void;
  /** At field 0, ← hands off to the previous artifact. No-op for a single form. */
  onPrevArtifact: () => void;
  /** Past the last field, → stages these answers and advances (or commits). */
  onAdvance: (values: Record<string, string>) => void;
  /** Escape, or the header's X, throws the overlay away (data loss accepted). */
  onDismiss: () => void;
  /**
   * The canvas took the form, so the whole of it is open beside this card. A
   * host that covers the canvas, like a dialog, gets out of its way here.
   */
  onOpenedOnCanvas?: () => void;
  /**
   * Files attached to the answer, which commit with it (BIG-9261). Absent where
   * the answer cannot carry files, and then the card offers no way to attach.
   */
  attachedFiles?: AttachedFile[];
  onFilesChange?: (update: AttachedFile[] | ((prev: AttachedFile[]) => AttachedFile[])) => void;
}) => {
  const fields = artifact.form?.fields ?? [];
  // The whole form, one click away — on the canvas, or the lightbox where there
  // is none. Offered only when there is more than this step to see.
  const {
    canvas,
    urn: artifactUrn,
    isLightboxOpen,
    setLightboxOpen,
    openForm,
  } = useOpenFormArtifact(artifact, threadId, "biscuit", onOpenedOnCanvas);
  // An unanswered form is a card in the review like any other, so the canvas
  // has to follow the carousel onto it. Without this the arrows moved the
  // carousel between a paused tool call and the question about it while the
  // canvas sat on whichever one it opened with, and both arrows looked dead.
  useCanvasFollowsCarousel(artifactUrn, canvas);
  const [values, setValues] = useState<Record<string, string>>(() => stagedValues ?? {});
  const [fieldIndex, setFieldIndex] = useState(0);
  // A multi-select answer is a list, so the answer box can't write straight into
  // `values` the way a single answer does — what's typed is held here until
  // advancing folds it into the selection.
  const [multiSelectDraft, setMultiSelectDraft] = useState("");

  const activeIndex = Math.min(fieldIndex, Math.max(0, fields.length - 1));
  const activeField: AskUserFormField | undefined = fields[activeIndex];
  const isLastField = activeIndex >= fields.length - 1;
  const isLastArtifact = pagination ? pagination.index === pagination.total - 1 : true;
  const isFirstStep = activeIndex === 0 && (!pagination || pagination.index === 0);

  const stage = useCallback(
    (next: Record<string, string>) => {
      setValues(next);
      onStageValues(next);
    },
    [onStageValues],
  );

  // → : next field, or past the last field hand off to the next artifact/commit.
  // Navigation is never blocked and answers are free text — the user can move
  // freely between questions and submit whatever they type. The server accepts
  // any answer; it only creates a contact/handle when the value is a real one.
  const goNext = useCallback(
    (current: Record<string, string>) => {
      if (isSubmitting) return;
      if (isLastField) onAdvance(current);
      else setFieldIndex((i) => i + 1);
    },
    [isSubmitting, isLastField, onAdvance],
  );

  // ← : previous field, or past the first field hand off to the previous artifact.
  const goPrev = useCallback(() => {
    if (isSubmitting) return;
    if (activeIndex > 0) setFieldIndex((i) => i - 1);
    else onPrevArtifact();
  }, [isSubmitting, activeIndex, onPrevArtifact]);

  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  // The composer's own attach machinery: picker, paste, drop, type checks.
  const canAttach = attachedFiles !== undefined && onFilesChange !== undefined;
  const canChangeFiles = canAttach && !isSubmitting;
  const {
    fileInputRef,
    supportedTypes,
    isDraggingOver,
    handleAttachClick,
    handleFileSelect,
    handlePaste,
    handleDragOver,
    handleDragLeave,
    handleDrop,
  } = useFileAttachment(onFilesChange ?? ignoreFiles);
  const setFieldValue = useCallback(
    (value: string) => activeField && stage({ ...values, [activeField.id]: value }),
    [activeField, stage, values],
  );

  // Picking an option is the fast path: it answers the question and moves
  // forward exactly like Next/Done — advancing to the next question, or
  // submitting when it's the last/only question (a single-select form becomes
  // one click). The free-text box is the manual path for an answer that isn't
  // one of the options.
  //
  // A multi-select is the exception: the user is still mid-answer after each
  // tap, so picking toggles and stays put. Enter / Next is what ends the
  // question, and it takes anything typed in the box along with the picks.
  const pickOption = useCallback(
    (value: string) => {
      if (!activeField || isSubmitting) return;
      if (activeField.type === "multiSelect") {
        const selected = decodeMultiSelectValue(values[activeField.id]);
        const toggled = selected.includes(value)
          ? selected.filter((v) => v !== value)
          : [...selected, value];
        stage({ ...values, [activeField.id]: encodeMultiSelectValue(toggled) });
        return;
      }
      const next = { ...values, [activeField.id]: value };
      stage(next);
      goNext(next);
    },
    [activeField, isSubmitting, stage, values, goNext],
  );

  // Focus the answer box on each step so the user can type immediately and the
  // keyboard nav (Enter/Tab/arrows on the box) is live without a manual click.
  // Also drops any uncommitted multi-select text, which belonged to the question
  // being left behind.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refocus per step.
  useEffect(() => {
    inputRef.current?.focus();
    setMultiSelectDraft("");
  }, [activeIndex]);

  const positionLabel = useMemo(() => {
    const artifactPart = pagination ? `${pagination.index + 1} of ${pagination.total} · ` : "";
    return `${artifactPart}Question ${activeIndex + 1} of ${fields.length}`;
  }, [pagination, activeIndex, fields.length]);

  if (!activeField) return null;

  const showDone = isLastField && isLastArtifact;
  // Select-type fields offer quick-pick options above the free-text box; every
  // other type is text only. The answer box is always present so the user can
  // type something other than an offered option.
  const isMultiSelect = activeField.type === "multiSelect";
  const options =
    activeField.type === "select" ||
    activeField.type === "selectHandle" ||
    activeField.type === "multiSelect"
      ? activeField.options
      : [];
  const multiSelected = isMultiSelect ? decodeMultiSelectValue(values[activeField.id]) : [];
  // Answers the user typed rather than picked. They render as chips beside the
  // offered options — without that, typing something and pressing Enter would
  // look like it did nothing.
  const customPicks = multiSelected.filter((value) => !options.some((o) => o.value === value));
  const inputType =
    activeField.type === "text" && activeField.inputType ? activeField.inputType : "text";

  /**
   * The answers as they stand, with anything typed in the box folded in.
   *
   * Advancing has to go through this rather than reading `values` directly:
   * committing the draft is a state update, so `values` is still the pre-typing
   * snapshot on this tick and would submit without what the user just typed.
   */
  const withTypedAnswer = (): Record<string, string> => {
    const typed = multiSelectDraft.trim();
    if (!isMultiSelect || !typed || multiSelected.includes(typed)) return values;
    return { ...values, [activeField.id]: encodeMultiSelectValue([...multiSelected, typed]) };
  };

  /** Fold in the typed answer, then move on — one keystroke, like a single select. */
  const commitAndAdvance = () => {
    const next = withTypedAnswer();
    if (next !== values) stage(next);
    setMultiSelectDraft("");
    goNext(next);
  };

  // Keyboard nav on the answer box: Enter / Tab advance, Shift+Tab goes back,
  // and ←/→ navigate only at the caret edges so mid-text editing isn't hijacked
  // (mirrors the queue's note-input handling).
  //
  // An open @mention or slash menu owns those keys — Enter picks the contact,
  // the arrows move through the list — so nothing here may act on them.
  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLTextAreaElement>,
    hasPopupOpen = false,
  ) => {
    if (isSubmitting || hasPopupOpen) return;
    // Number shortcuts (1..N) pick a select option — but only while the answer
    // box is empty, so a digit the user is typing as their answer is literal.
    if (options.length > 0 && e.currentTarget.value === "" && !e.metaKey && !e.ctrlKey) {
      const n = Number.parseInt(e.key, 10);
      if (Number.isInteger(n) && n >= 1 && n <= options.length) {
        e.preventDefault();
        pickOption(options[n - 1].value);
        return;
      }
    }
    if (e.key === "Tab") {
      e.preventDefault();
      if (e.shiftKey) goPrev();
      else commitAndAdvance();
      return;
    }
    // Shift+Enter is a new line in a free-text answer; plain Enter still advances.
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      commitAndAdvance();
      return;
    }
    const el = e.currentTarget;
    // number/date inputs report a null selection — there's no caret to preserve,
    // so treat them as always at both edges (arrows navigate freely).
    const atStart =
      el.selectionStart === null || (el.selectionStart === 0 && el.selectionEnd === 0);
    const atEnd =
      el.selectionStart === null ||
      (el.selectionStart === el.value.length && el.selectionEnd === el.value.length);
    if (e.key === "ArrowRight" && atEnd) {
      e.preventDefault();
      commitAndAdvance();
    } else if (e.key === "ArrowLeft" && atStart) {
      e.preventDefault();
      goPrev();
    }
  };

  const answer = isMultiSelect ? multiSelectDraft : (values[activeField.id] ?? "");
  const setAnswer = (value: string) =>
    isMultiSelect ? setMultiSelectDraft(value) : setFieldValue(value);
  // "Or" only when there are offered options to type instead of; a
  // multi-select always offers some.
  const placeholder =
    isMultiSelect || options.length > 0 ? "Or type your own answer…" : "Type your answer…";
  const attachButton = (className: string) =>
    canAttach && (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={handleAttachClick}
        disabled={isSubmitting}
        aria-label={ARIA_LABELS.ATTACH_FILES}
        className={cn("shrink-0 rounded-full hover:bg-secondary", className)}
        data-statsig-name="omni-form-attach"
      >
        <Plus className="size-4" />
      </Button>
    );

  return (
    <OmniCardChrome onDismiss={onDismiss}>
      {/* The 5px pair is the icon buttons' own inset, as on the biscuits' header:
          `ps` starts the text where their glyphs sit, `-mb` hands back the
          overhang the taller row adds below it. Without it the counter and the
          question below it shift 5px as the queue pages between this card and a
          biscuit. */}
      <div className="flex items-center justify-between text-xs text-muted-foreground ps-[5px] -mb-[5px]">
        <span className="tabular-nums">
          {positionLabel}
          {pagination && decidedCount !== undefined && ` · ${decidedCount} decided`}
        </span>
        <div className="flex items-center gap-1">
          {/* Drawn only where there is somewhere to go: the first question of the
              first card has neither a previous question nor a previous card.

              It leads the group, which is what makes its absence free: hidden
              from the middle it would shove whatever sits to its left
              sideways. */}
          {!isFirstStep && (
            <Button
              variant="ghostPopover"
              size="action"
              onClick={goPrev}
              disabled={isSubmitting}
              aria-label="Previous question"
              data-statsig-name="omni-form-prev"
            >
              <ChevronLeft className="size-4" />
            </Button>
          )}
          {showDone ? (
            <Button
              variant="secondary"
              size="xs"
              onClick={commitAndAdvance}
              disabled={isSubmitting}
              data-statsig-name="omni-form-done"
            >
              {isSubmitting ? "Submitting…" : "Done"}
            </Button>
          ) : (
            <Button
              variant="ghostPopover"
              size="action"
              onClick={commitAndAdvance}
              disabled={isSubmitting}
              aria-label="Next question"
              data-statsig-name="omni-form-next"
            >
              <ChevronRight className="size-4" />
            </Button>
          )}
          {/* Between the terminal button and dismiss — the slot the biscuits
              give it. The group is anchored right, so a control sitting in a
              different slot here lands under the pointer of someone paging a
              mixed queue: they aim at next on a biscuit and hit expand on this
              card. Same reason it wears the biscuits' arrow and their
              `Expand <artifact>` name rather than a maximise glyph and a verb of
              its own: it is the same control, so it reads as one.

              Drawn unconditionally for that reason, a one-question form
              included. Expanding one question shows little the card does not,
              but a card that omits the control is a card whose whole group
              shifts a slot left, which puts prev where every other card in the
              queue puts next. A control that adds little beats a control that
              moves. */}
          <OmniCardExpandButton
            expand={{ onExpand: openForm, label: `Expand ${FORM_ARTIFACT_TITLE}` }}
            disabled={isSubmitting}
            statsigName="omni-form-open-full"
          />
          <OmniDismissButton
            onDismiss={onDismiss}
            disabled={isSubmitting}
            statsigName="omni-form-dismiss"
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Label
          htmlFor={activeField.id}
          className="text-sm @md:text-base font-normal text-foreground"
        >
          <InlineMarkdown>{activeField.question}</InlineMarkdown>
        </Label>

        {options.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {options.map((opt, optIdx) => {
              const isSelected = isMultiSelect
                ? multiSelected.includes(opt.value)
                : (values[activeField.id] ?? "") === opt.value;
              // Number shortcut for the first 9 options (typeable digits).
              const shortcut = optIdx < 9 ? optIdx + 1 : undefined;
              return (
                <Button
                  key={opt.value}
                  type="button"
                  variant={isSelected ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => pickOption(opt.value)}
                  disabled={isSubmitting}
                  data-statsig-name="omni-form-option"
                  className={cn(
                    // Override the Button base `whitespace-nowrap` + fixed `h-9` so long
                    // option labels wrap instead of overflowing the popover. `min-h-9`
                    // keeps short options visually identical to single-line buttons.
                    "h-auto min-h-9 gap-2 whitespace-normal py-1.5 pl-[7px] text-left font-normal",
                    isSelected && "border-primary",
                  )}
                >
                  {shortcut !== undefined && (
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-medium text-accent-foreground">
                      {shortcut}
                    </span>
                  )}
                  <InlineMarkdown>{opt.label}</InlineMarkdown>
                </Button>
              );
            })}
            {/* Answers the user typed. Same chip treatment as the offered
                  options — once banked there's no reason to distinguish them —
                  and clicking removes, since there's no un-type. */}
            {customPicks.map((value) => (
              <Button
                key={value}
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => pickOption(value)}
                disabled={isSubmitting}
                data-statsig-name="omni-form-custom-option"
                className="h-auto min-h-9 gap-2 whitespace-normal border-primary py-1.5 text-left font-normal"
              >
                {value}
              </Button>
            ))}
          </div>
        )}

        {/* Always-present answer box: the primary input for text questions and
              a free-text override for every kind of select. Focused on every
              step (see the effect above) so keyboard entry works immediately.

              On a multi-select it writes to its own draft rather than straight
              into `values` — that slot holds the encoded list — and Enter banks
              the draft as another pick instead of advancing. */}
        {/* Attachments sit with the answer they belong to, as the composer's
              do, so "I'll attach my own screenshot" can be answered here
              (BIG-9261). Dropping anywhere on the answer area attaches.

              Frozen while submitting — drop, picker, and chip removal alike:
              the commit has already taken its copy of the files, so a chip
              removed now would still be sent and a file added now would be
              cleared without ever being sent. */}
        <div
          className="relative flex flex-col gap-2"
          onDragOver={canChangeFiles ? handleDragOver : undefined}
          onDragLeave={canChangeFiles ? handleDragLeave : undefined}
          onDrop={canChangeFiles ? handleDrop : undefined}
        >
          {canChangeFiles && isDraggingOver && (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-[1.25rem] border-2 border-dashed border-primary bg-popover/90 text-sm font-medium text-primary">
              <Paperclip className="size-4" />
              Drop to attach
            </div>
          )}
          {canAttach && (
            <FileAttachment
              attachedFiles={attachedFiles}
              onFilesChange={onFilesChange}
              readOnly={isSubmitting}
            />
          )}
          {inputType === "text" ? (
            // The composer's own text box (BIG-9261): @mentions with contact
            // autocomplete, slash commands, and growth to ten lines before it
            // scrolls. Drawn as the composer's pill, attach control inside.
            //
            // `initialValueIsTyped` because a value present at mount is an
            // answer restored from staging, which the box must not treat as a
            // prefill to expand into the scheduling flow. That guard covers
            // only the value a box mounts with, so each question gets its own
            // box (`key`): a reused one would take the next question's answer
            // as a prefill and rewrite "Schedule a meeting" into "…with ".
            //
            // The vertical inset lives on the pill, never on the field: the
            // mention overlay is pinned to the field's box with no padding of
            // its own, so a padded field draws its text below the overlay's.
            // 9px each side centres the 20px line in 40px; the attach button's
            // `-my-1.5` lets it stay 32px without stretching the pill, centred
            // on the last line as the box grows.
            <div
              className={cn(
                "flex min-h-10 items-end gap-2 rounded-[1.25rem] border border-input bg-popover py-[9px] pr-3",
                canAttach ? "pl-[3px]" : "pl-3",
              )}
            >
              {attachButton("size-8 -my-1.5")}
              <SmartTextarea
                key={activeField.id}
                id={activeField.id}
                ref={inputRef}
                value={answer}
                onChange={setAnswer}
                onKeyDown={handleInputKeyDown}
                onPaste={canAttach ? handlePaste : undefined}
                disabled={isSubmitting}
                placeholder={placeholder}
                enterKeyHint="next"
                aria-label="Your answer"
                maxRows={10}
                initialValueIsTyped
                // `block` drops the inline line-box gap that sat under the
                // field and pushed its text off-centre.
                className="block"
              />
            </div>
          ) : (
            <div className="flex items-end gap-2">
              {attachButton("size-10")}
              <Input
                id={activeField.id}
                ref={inputRef}
                type={inputType}
                value={answer}
                onChange={(e) => setAnswer(e.currentTarget.value)}
                onKeyDown={(e) => handleInputKeyDown(e)}
                onPaste={canAttach ? handlePaste : undefined}
                disabled={isSubmitting}
                placeholder={placeholder}
                enterKeyHint="next"
                className="w-full bg-popover text-popover-foreground"
              />
            </div>
          )}
          {canAttach && (
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              // A picker opened before the submit started can still return
              // during it. Its pick is dropped, as a drop would be, and the
              // input cleared so choosing the same file again still registers.
              onChange={(e) => {
                if (canChangeFiles) handleFileSelect(e);
                else e.currentTarget.value = "";
              }}
              accept={supportedTypes || UI_CONSTANTS.DEFAULT_ACCEPT_TYPES}
            />
          )}
        </div>
      </div>

      {/* Fallback surface where there is no canvas (mobile / gate off) — the same
          whole form the canvas would have presented. */}
      <FormArtifactLightbox
        artifact={artifact}
        threadId={threadId}
        open={isLightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    </OmniCardChrome>
  );
};
